import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useValidateTimeRecordsUpToDate,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { events } from "@planzone/events";
import { Props } from "./types";
import { isValidated } from "./utils";
import { CheckMark } from "./checkMark";
import {
  parseAsLocalDate,
  parseTimestamp,
  toFixed,
  utcDt,
} from "@pages/sharedTimeSheetUtils";
import styles from "./styles.scss";
import { useTranslation } from "react-i18next";

function Total({ project, dates, timeSheet, areDetailsDisplayed }: Props) {
  const { t } = useTranslation();
  const { isRequestPending, validateTimeRecordsUpToDate } =
    useValidateTimeRecordsUpToDate(project);
  const [integrationDate, setIntegrationDate] = React.useState<DateTime | null>(
    !!project.integratedAt ? parseTimestamp(project.integratedAt) : null,
  );
  const hasIntegrationDate = integrationDate !== null;
  const [hoveredDate, setHoveredDate] = React.useState<DateTime | null>(null);
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions(project);
  const canEdit =
    isAdministrator ||
    (isProjectManager && settings.canApproveProjectPersonalTimeSheet) ||
    settings.canApproveManagerPersonalTimeSheet;

  React.useEffect(() => {
    if (project.integratedAt) {
      const nextDate = parseTimestamp(project.integratedAt);
      if (integrationDate === null || !integrationDate.equals(nextDate)) {
        setIntegrationDate(nextDate);
      }
    }
  }, [project.integratedAt]);

  useMessage(({ type, data }) => {
    if (settings.areTimeSheetsAutoIntegrated) {
      if (type === events.saveTimeRecordSucceeded) {
        const recordDate = utcDt(
          data.timeRecord.year,
          data.timeRecord.month - 1,
          data.timeRecord.date,
        );

        if (integrationDate === null || recordDate > integrationDate) {
          setIntegrationDate(recordDate);
        }
      }
    }
  });

  return (
    <div className={styles.total}>
      {timeSheet.spentTimes.map(({ date: d, time }) => {
        const date = parseAsLocalDate(d);
        const validated =
          hasIntegrationDate && isValidated(integrationDate, date);
        const pendingValidation = !!hoveredDate && date <= hoveredDate;
        const hovered = !!hoveredDate && date.equals(hoveredDate);

        return (
          <div
            key={date.toISO() ?? ""}
            className={classNames(styles.day, {
              [styles.validated]: validated,
              [styles.pendingValidation]: pendingValidation,
              [styles.hovered]: hovered,
            })}
            onMouseEnter={() =>
              canEdit &&
              !settings.areTimeSheetsAutoIntegrated &&
              setHoveredDate(date)
            }
            onMouseLeave={() =>
              canEdit &&
              !settings.areTimeSheetsAutoIntegrated &&
              setHoveredDate(null)
            }
          >
            <div className={styles.record}>
              {!settings.areTimeSheetsAutoIntegrated && (
                <div
                  className={styles.button}
                  onClick={() =>
                    canEdit &&
                    !isRequestPending &&
                    validateTimeRecordsUpToDate({
                      project,
                      date: date.toUTC().toISO() ?? "",
                    })
                  }
                >
                  <CheckMark
                    className={styles.checkMark}
                    isChecked={!!hoveredDate ? hoveredDate >= date : validated}
                  />
                </div>
              )}
              <p className={styles.text}>{toFixed(time)}</p>
            </div>
          </div>
        );
      })}
      {areDetailsDisplayed && (
        <div className={classNames(styles.day, styles.deadline)}>
          {timeSheet.timesheetDeadline && (
            <p className={styles.text}>
              {t("timeSheet.lockDate")} :{" "}
              {DateTime.fromISO(timeSheet.timesheetDeadline, { zone: "utc" })
                .toLocal()
                .toLocaleString(DateTime.DATE_SHORT)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { Total };
